// Máquina de estados do envio no Chat (uma por conversa aberta, só em memória).
//
//   idle ──submit──► streaming ──settle(ok, nada pedido)──► streaming (próximo da fila) ──► … ──► idle
//                        │
//                        ├─ stop ───────────────► streaming (cancelando) ──settle──► paused (se sobrou fila) | idle
//                        ├─ sendNow(item) ──────► streaming (cancelando) ──settle──► streaming (o item) …
//                        └─ erro / conexão caída ─settle(falha)──────────────────► paused (se sobrou fila) | idle
//   paused ──resume──► streaming · paused ──clear──► idle · paused ──sendNow(item)──► streaming (um item; depois volta a paused)
//
// Invariantes: `streaming` ⇔ há `run`; `paused` ⇒ não há `run` e a fila não está vazia; `idle` ⇒ sem `run` e sem fila.
// Tudo aqui é síncrono e sem efeito colateral: cada transição devolve os `Effect`s que quem chama deve executar
// (iniciar um envio, cancelar um job). Assim "terminou" e "o usuário clicou" nunca se intercalam: o que chega depois
// enxerga o estado que o anterior deixou. Este arquivo não importa nada de propósito (roda direto no Node nos testes).

export const QUEUE_MAX = 5;

/** Um envio capturado no momento em que o usuário apertou Enter. */
export type Item = { key: number; text: string; images: string[]; documentIds: string[]; model: string };
/** O que fazer quando o envio em curso terminar sem erro: seguir a fila (`drain`) ou parar nela (`hold`, depois de "enviar agora" numa fila pausada). */
export type After = 'drain' | 'hold';
/** O que o usuário pediu para o envio em curso: nada; parar (pausa a fila); ou trocá-lo por outro item agora. */
export type Intent = { kind: 'none' } | { kind: 'pause' } | { kind: 'replace'; item: Item; after: After };
export type Run = {
  id: number;
  item: Item;
  jobId: string | null;
  stage: 'live' | 'closing';   // `closing`: o servidor já mandou done/error; cancelar não faz mais sentido
  cancelled: boolean;          // o pedido de cancelamento já saiu (uma vez só)
  intent: Intent;
  after: After;
};
export type Phase = 'idle' | 'streaming' | 'paused';
export type Machine = { phase: Phase; run: Run | null; queue: Item[]; seq: number };
export type Effect = { kind: 'start'; run: Run } | { kind: 'cancel'; jobId: string };

export const createMachine = (): Machine => ({ phase: 'idle', run: null, queue: [], seq: 0 });

function begin(m: Machine, item: Item, after: After): Effect[] {
  const run: Run = { id: ++m.seq, item, jobId: null, stage: 'live', cancelled: false, intent: { kind: 'none' }, after };
  m.run = run; m.phase = 'streaming';
  return [{ kind: 'start', run }];
}

/** Cancela no servidor se o usuário pediu, o job já existe e ainda está vivo; no máximo uma vez por envio. */
function wantCancel(run: Run): Effect[] {
  if (run.stage !== 'live' || run.cancelled || !run.jobId || run.intent.kind === 'none') return [];
  run.cancelled = true;
  return [{ kind: 'cancel', jobId: run.jobId }];
}

/** Enter com texto. `null` = fila cheia (nada mudou). Sem envio em curso o item é enviado na hora. */
export function submit(m: Machine, item: Item): Effect[] | null {
  if (m.phase === 'idle') return begin(m, item, 'drain');
  // o item de um "enviar agora" ainda não despachado conta no limite: se "parar" o devolver à fila, ela não pode passar de 5
  if (m.queue.length + (m.run?.intent.kind === 'replace' ? 1 : 0) >= QUEUE_MAX) return null;
  m.queue.push(item);
  return [];
}

/** O servidor aceitou o job (evento `accepted`). Se o usuário já pediu para interromper, o pedido sai agora. */
export function accepted(m: Machine, run: Run, jobId: string): Effect[] {
  if (m.run !== run) return [];
  run.jobId = jobId;
  return wantCancel(run);
}

/** Chegou `done`/`error`: o job acabou no servidor. */
export function closing(m: Machine, run: Run): void { if (m.run === run) run.stage = 'closing'; }

/** "Parar": interrompe só o envio em curso e pausa a fila. Sem efeito fora de `streaming`. */
export function stop(m: Machine): Effect[] {
  const run = m.run;
  if (m.phase !== 'streaming' || !run) return [];
  if (run.intent.kind === 'replace') m.queue.unshift(run.intent.item);   // o "enviar agora" ainda não saiu: o item volta ao começo da fila
  run.intent = { kind: 'pause' };
  return wantCancel(run);
}

/** "Enviar agora" num item da fila. Item que já não está na fila (despachado ou apagado um instante antes) = nada. */
export function sendNow(m: Machine, key: number): Effect[] {
  const i = m.queue.findIndex((x) => x.key === key);
  if (i < 0) return [];
  const [item] = m.queue.splice(i, 1);
  if (m.phase === 'paused') return begin(m, item, 'hold');
  const run = m.run;
  if (m.phase !== 'streaming' || !run) { m.queue.splice(i, 0, item); return []; }
  const prev = run.intent;
  if (prev.kind === 'replace') m.queue.unshift(prev.item);   // o último clique vence; o anterior volta ao começo da fila
  const after: After = prev.kind === 'pause' ? 'hold' : prev.kind === 'replace' ? prev.after : run.after;
  run.intent = { kind: 'replace', item, after };
  return wantCancel(run);
}

/** "Apagar" um item. Se a fila pausada esvazia, volta a `idle`. */
export function remove(m: Machine, key: number): void {
  const i = m.queue.findIndex((x) => x.key === key);
  if (i < 0) return;
  m.queue.splice(i, 1);
  if (m.phase === 'paused' && !m.queue.length) m.phase = 'idle';
}

/** "Continuar" na fila pausada: retoma do primeiro item. */
export function resume(m: Machine): Effect[] {
  if (m.phase !== 'paused' || !m.queue.length) return [];
  return begin(m, m.queue.shift()!, 'drain');
}

/** "Limpar" na fila pausada: descarta tudo sem enviar. */
export function clear(m: Machine): void {
  if (m.phase !== 'paused') return;
  m.queue = []; m.phase = 'idle';
}

/** O envio `run` terminou (`ok` = chegou `done`). Decide o que vem depois; nunca duas respostas ao mesmo tempo. */
export function settle(m: Machine, run: Run, ok: boolean): Effect[] {
  if (m.run !== run) return [];   // envio descartado (a conversa mudou) ou já resolvido
  m.run = null;
  const it = run.intent;
  if (it.kind === 'replace') return begin(m, it.item, it.after);   // "enviar agora" vale mesmo que o envio interrompido tenha falhado
  if (ok && it.kind === 'none' && run.after === 'drain' && m.queue.length) return begin(m, m.queue.shift()!, 'drain');
  m.phase = m.queue.length ? 'paused' : 'idle';
  return [];
}

/** Troca de conversa / saída da página: a fila some e o envio em curso deixa de valer. Devolve o envio descartado. */
export function reset(m: Machine): Run | null {
  const run = m.run;
  m.run = null; m.queue = []; m.phase = 'idle';
  return run;
}
